import React, {
  useEffect,
  useRef,
  useState,
  memo,
  type Dispatch,
  type SetStateAction,
  type InputHTMLAttributes,
  type ChangeEvent,
  type FocusEvent,
  type ReactNode,
} from "react";
import { Text, Box, TextAreaField } from "@cruk/cruk-react-components";

import { validate, calcLength } from "@fwa/src/utils/formUtils";

import { type ValidationType } from "@fwa/src/types";

// This component is more streamlined version the EditableText component
// It is based on a TextAreaField, which is a standard text textArea element
// so doesn't need any of the complexity of selecting ranges
// or updating inner html to keep new lines consistent across different browsers
export const EditableTextAreaField = ({
  setCurrentValue,
  text,
  validation,
  setValidationMessage,
  validationMessage = "",
  label = "",
  hintText,
  lineCount = 3,
  "aria-label": ariaLabel,
  ref,
  name,
  id,
}: InputHTMLAttributes<HTMLTextAreaElement> & {
  setCurrentValue: (value: string) => void;
  text: string;
  validation: ValidationType;
  setValidationMessage: Dispatch<SetStateAction<string>>;
  label?: string;
  hintText?: ReactNode;
  validationMessage?: string;
  lineCount?: number;
  ref?: React.RefObject<HTMLTextAreaElement | null>;
}) => {
  const [charCount, setCharCount] = useState(calcLength(text || ""));
  const fallBackRef = useRef<HTMLTextAreaElement>(null);
  const textAreaRef = ref || fallBackRef;

  const handleBlur = (e: FocusEvent<HTMLTextAreaElement>) => {
    validate(e.target.value, validation, setValidationMessage);
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(calcLength(e.target.value || text || ""));
    setCurrentValue(e.target.value);
  };

  // reset validation message on first render
  useEffect(() => {
    setValidationMessage("");
  }, [setValidationMessage]);

  useEffect(() => {
    if (text === "" && textAreaRef.current) {
      textAreaRef.current.value = "";
    }
  }, [text, textAreaRef]);

  return (
    <>
      <TextAreaField
        id={id}
        ref={textAreaRef}
        label={label}
        aria-label={ariaLabel}
        onBlur={handleBlur}
        onChange={handleChange}
        defaultValue={text}
        errorMessage={validationMessage}
        hintText={hintText}
        maxLength={validation?.maxLength || undefined}
        lineCount={lineCount}
        name={name || ariaLabel || label || "editable-textarea-field"}
      />

      {validation.maxLength && validation.maxLength > 0 && (
        <Box marginBottom="s">
          <Text textAlign="right" marginTop="xxs">{`${
            validation.maxLength - charCount
          } characters remaining`}</Text>
        </Box>
      )}
    </>
  );
};

export default memo(EditableTextAreaField);
